import { useState, useMemo, useRef, useDeferredValue } from "react";
import { useMediaQuery } from "usehooks-ts";
import { useFetch } from "hooks/api";
import { Col, Container as Grid, Row, Badge, NavDropdown, Card } from "react-bootstrap";
import { Button, ButtonGroup } from "@material-ui/core";
import store from "redux/store";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "utils/formatNumber";
import { trellisPalette } from "components/custom/analytics/palettes";
import Accordion from "components/core/basic/Accordion";
import useColumnSelector from "hooks/useColumnSelector";
import { filterRanges, generateFilterMarkup, generateRanges } from "utils/rangeDropdownFilter";
import ReactTable from "react-table-6";
import ReactTablePagination from "components/custom/growth/ProductsCVTablePagination";
import { getURLPrefix } from "utils/getUrlPrefix";
import NoImage from "assets/images/utility/no_image.png";
import { useHistory, useParams } from "react-router-dom";
import DownloadReport from "modules/perf_reports/DownloadReport";
import { DatesProvider } from "dates/DatesContext";
import { useDates } from "dates/DatesContext";
import moment from "moment";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import PromotionsCard from "components/custom/merchandise/cards/PromotionsCard";
import ExpanderCell from "components/custom/merchandise/cells/ExpanderCell";
import TrComponent from "components/custom/merchandise/TrComponent";
import ProductCard from "components/custom/merchandise/ProductCard";
import PricingCard from "components/custom/merchandise/cards/PricingCard";
import getMetricSpec, { buildMetric } from "./metrics";
import NPICard from "components/custom/merchandise/cards/NPICard";
import {
  ADVERTISING,
  CONTENT,
  DEALS,
  DYNAMIC_PRICING,
  NEW_PRODUCT_LAUNCH,
} from "components/custom/merchandise/constants";
import checkApplication from "utils/checkApplication";
import Loading from "components/core/blocks/Loading";
import ContentCard from "components/custom/merchandise/cards/ContentCard";
import { setMerchandiseDashboard } from "redux/reducers/userReducer";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import api from "utils/api";
import EmptyState from "components/custom/merchandise/EmptyState";

import MetricsCard from "components/custom/merchandise/MetricsCard";
import DetailedMetricRow from "components/core/blocks/DetailedMetricRow";
import AdvertisingIcon from "assets/images/icons/placement.svg";
import ThemeLine from "components/custom/merchandise/ThemeLine";
import { selectModules, useModuleEnabled } from "redux/selectors/modules";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import useCategories from "hooks/useCategories";
import useMerchandisePreferences from "hooks/useMerchandisePreferences";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import useTableSettings from "hooks/useTableSettings";
import { RT6FilterWidget } from "./tables/FilterWidget";
import {
  INVENTORY_STATE_FILTER,
  SALES_STATE_FILTER,
  PRODUCT_WITH_ADS_FILTER,
  buildCustomTagsFilter,
} from "views/advertising/productLifecycleFilters";
import VerifyEmailModal from "views/onboarding/components/VerifyEmailModal";
import useIsSPAPIConnected from "hooks/useIsSPAPIConnected";
import AnnotationsTable from "components/custom/growth/AnnotationsTable";
import SalesMapUSA from "components/custom/maps/usa";
import { Redirect } from "react-router-dom";

import {
  LEGACY_METRIC_IDS_MAP,
  extendAllEntityMetricOptions,
  useStandardEntityMetrics,
} from "views/merchandise/tables/metricUtils";
import { METRIC_GROUPING_LOOKUP } from "./tables/metricsByEntity";
import { sortTableColumns } from "utils/tableUtils";
import { useAppSelector } from "../../redux/hooks";
import { ProductCell } from "./cells/ProductCell";
import { DashboardLink } from "./cells/ProductCell";
import { getDashboardTo, DASHBOARD_TYPES } from "./cells/ProductCell";
import Tags from "components/core/blocks/Tags";
import ExtensionInfo from "views/onboarding/components/ExtensionInfo";
import AvailableProfilesModal from "./AvailableProfilesModal";
import { css, cx } from "../../../styled-system/css";
import TrellisLink from "components/core/basic/TrellisLink";
import { SIDEBAR_COLORS } from "hooks/useDashboardTheme";
import ProductsTable from "./tables/ProductsTable";
import { TrendCell } from "views/merchandise/cells/TrendCell";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "components/table/utils";
import { createColumnBuilder } from "components/table/columnBuilder";
import { useGetV4ProductReportByDate } from "modules/perf_reports/api/product/v4ProductReportByDate";
import { useGetV4ProductReportTotals } from "modules/perf_reports/api/product/v4ProductReportTotals";
import { Flex } from "../../../styled-system/jsx";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";

const CARDS = [
  {
    id: "advertising",
    name: "Advertising",
    app: ADVERTISING,
    color: SIDEBAR_COLORS.advertising,
    link: "/user/advertising/dashboard",
    categoryLink: (id) => {
      return `/user/advertising/dashboard/category/${id}`;
    },
  },
  {
    id: "dynamic_pricing",
    name: "Dynamic Pricing",
    app: DYNAMIC_PRICING,
    color: SIDEBAR_COLORS.pricing,
    link: "/user/_pricing/dashboard",
    categoryLink: (id) => `/user/_pricing/management/category/${id}`,
  },
  {
    id: "deals",
    name: "Deals",
    app: DEALS,
    link: "/user/merchandising/promotions",
    color: SIDEBAR_COLORS.promotions,
    categoryLink: () => `/user/merchandising/promotions`,
  },
  {
    id: "content",
    name: "Content",
    app: CONTENT,
    link: "/user/content/quality/org",
    color: SIDEBAR_COLORS.product,
    categoryLink: (id) => `/user/content/quality/category/${id}`,
  },
  { id: "npi", name: "New Product Introductions", app: NEW_PRODUCT_LAUNCH, link: "/user/npi/dashboard" },
];

const columnHelper = createColumnBuilder();

const URL_PREFIX = getURLPrefix();

const ExpandedContent = ({ marketPlace, row, user, channel }) => {
  return (
    <div style={{ backgroundColor: "#efefef" }}>
      <ProductCard marketPlace={marketPlace} productId={row?.original?.product_id} user={user} channel={channel} />
    </div>
  );
};

const CategoryCell = (props) => {
  const user = useAppSelector((state) => state.user);
  const cards = useMemo(() => {
    return CARDS.map((c) => ({
      ...c,
    })).filter((c) => checkApplication(user, c.app));
  }, [user]);
  const categoryId = props?.original?.category_id;
  const image = `${URL_PREFIX}/api/productimage/?category=${categoryId}`;
  return (
    <div style={{ display: "flex" }}>
      <DashboardLink category={categoryId} date={props.date}>
        <div
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            flexShrink: 0,
            width: 50,
            height: 50,
            marginRight: "10px",
            background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
          }}
          draggable="false"
        />
      </DashboardLink>
      <div>
        <div>
          <DashboardLink category={categoryId} date={props.date}>
            {props?.value}
          </DashboardLink>
        </div>
        <div>
          {cards
            .filter((c) => c.categoryLink)
            .map((card, i) => (
              <span key={card.id}>
                {i > 0 && (
                  <span key={i} style={{ color: "#ddd", marginRight: "5px" }}>
                    |
                  </span>
                )}
                <TrellisLink
                  key={card.id}
                  to={card.categoryLink(categoryId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css({
                    color: "#888",
                    textDecoration: "none",
                    fontSize: "small",
                    display: "inline-block",
                    marginRight: "xsmall",
                    fontWeight: "normal",
                    _hover: {
                      color: "var(--hover-color)",
                      fontWeight: "bold",
                    },
                  })}
                  style={{
                    "--hover-color": card.color,
                  }}
                >
                  {card.name}
                </TrellisLink>
              </span>
            ))}
        </div>
      </div>
    </div>
  );
};
const col = (header, type, key, options) => ({
  header,
  type,
  key,
  options,
});

const makeColumn = (data, marketPlace, mobileStyle, date, setIsExpanded, modules) => (header, key, type, options) => {
  const { hideFilter } = options ?? {};

  const defaultAccessor = (p) => p[key];
  const accessor = options?.accessor ?? defaultAccessor;
  const defaultFormat = ({ value }) => value;
  const fmt =
    options?.formatter ??
    {
      expander: ({ row, value }) => (
        <ExpanderCell
          row={row}
          // TODO: Make expander value more versatile,
          value={row?._original?.product_id}
          isExpanded={value}
          setIsExpanded={setIsExpanded}
        />
      ),

      currency: ({ value }) => formatCurrency(value, marketPlace.marketPlace, options?.clipDecimalPlaces ?? true),
      number: ({ value }) => formatNumber(value, { decimalPlaces: 1 }),
      percent: ({ value }) => formatPercent(value, 1),
      category: CategoryCell,
      product: ProductCell,
      trends: (props) => {
        const { trends, trends_score } = props.original;
        return <TrendCell trends={trends} value={trends_score} />;
      },
      percentDiff: ({ value }) => (
        <span
          style={{
            color: value > 0 ? trellisPalette[12] : trellisPalette[17],
          }}
        >
          {formatPercent(value, 1, true)}
        </span>
      ),
    }?.[type] ??
    defaultFormat;
  const parser =
    {
      currency: parseFormattedCurrency,
      number: parseFormattedNumber,
      percent: parseFormattedPercentage,
    }?.[type] ?? defaultFormat;

  let width;
  if (mobileStyle) {
    width = options?.mobileWidth;
  } else {
    width = options?.width;
  }

  // Handle tooltips to support metric selection menu descriptions
  let { toolTip } = getMetricSpec(marketPlace, key);
  if (!toolTip) {
    const alternateMetricSpec = getMetricSpec(marketPlace, LEGACY_METRIC_IDS_MAP[key]);
    toolTip = alternateMetricSpec?.toolTip;
  }

  // Handle percent filter formatting
  if (type === "percent") {
    data = data.map((row) => {
      Object.keys(row).forEach((propertyKey) => {
        if (propertyKey === key) {
          row["formatted_" + propertyKey] = row[propertyKey] * 100;
        }
      });

      return row;
    });
  }

  return {
    Header: header,
    id: key,
    accessor,
    checked: options?.checked ?? true,
    maxWidth: options?.maxWidth,
    minWidth: options?.minWidth,
    isStatic: options?.isStatic,
    width,
    grouping: METRIC_GROUPING_LOOKUP[key],
    height: 30,
    format: type,
    toolTip,
    className: options?.className ?? "text-end",
    headerClassName: options?.className ?? "text-end",
    Cell: (props) => {
      if (options?.showDot ?? true) {
        const percentKey = `${key}_percent`;
        const percent = props.original?.[percentKey] ?? null;
        const deltaKey = `${key}_delta`;
        const deltaValue = props.original?.[deltaKey];
        const isNegative = options?.isNegative ?? false;
        const noColor = options?.noColor ?? false;
        let color = "#666";
        if (!noColor && percent > 0) {
          color = isNegative ? trellisPalette[17] : trellisPalette[12];
        }
        if (!noColor && percent < 0) {
          color = isNegative ? trellisPalette[12] : trellisPalette[17];
        }
        return (
          <div>
            {fmt({ ...props, date, modules })}
            <br />
            <span
              style={{
                color,
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              {!options?.hideCompare &&
                (options?.delta ? (deltaValue > 0 ? "+" : "") + fmt({ value: deltaValue })
                : percent === null ? ""
                : formatPercent(percent, 1, true))}
            </span>
          </div>
        );
      }
      return fmt({ ...props, date, modules });
    },

    style: { height: "60px" },
    filterMethod: (filter, row) => {
      const formattedPercentAccessor = (p) => p[`formatted_${key}`];
      const value = type === "percent" ? formattedPercentAccessor(row._original) : accessor(row._original);
      return filterRanges(filter, value, parser, marketPlace.marketPlace);
    },
    Filter: ({ filter, onChange }) => {
      if (hideFilter) {
        return null;
      }

      return generateFilterMarkup(filter, onChange, key, "range", type, {
        ranges: generateRanges(
          data ?? [],
          5,
          type === "percent" ? `formatted_${key}` : key,
          "number",
          marketPlace.marketPlace,
        ),
        marketPlace: marketPlace.marketPlace,
      });
    },
  };
};

const Table = ({
  stats,
  marketPlace,
  mobileStyle,
  category,
  columnSpecs,
  date,
  ExpandedContent,
  user,
  channel,
  isLoading,
  tableId,
}) => {
  const modules = useAppSelector(selectModules);
  const [searchFilter, setSearchFilter] = useState("");
  const [isExpanded, setIsExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [colToggle] = useState();

  const TABLE_ID =
    tableId ? tableId
    : category ? "merchProductsTable"
    : "merchCategoriesTable";
  const tableRef = useRef();

  const { customFilters, setCustomFilters, applyCustomFilters } = useCustomTableFilters(TABLE_ID);

  const { savedTableSettings: savedTablePageSize, updateTableSettings: setSavedTablePageSize } = useTableSettings(
    TABLE_ID,
    "tablePageSize",
    5,
  );

  const { savedTableSettings: savedTableSortMethod, updateTableSettings: setSavedTableSortMethod } = useTableSettings(
    TABLE_ID,
    "tableSorting",
    [],
  );

  const { savedTableSettings: savedColumnOrder, updateTableSettings: setSavedColumnOrder } = useTableSettings(
    TABLE_ID,
    "tableColOrder",
    [],
  );

  const customTags = useMemo(() => {
    return stats?.map((product) => product.tags)?.flat() ?? [];
  }, [stats]);

  const data = useMemo(
    () => {
      return applyCustomFilters(
        stats
          ?.map((p) => (p.product_id === isExpanded ? { ...p, isExpanded } : p))
          ?.filter((p) => {
            if (searchFilter) {
              const searchable =
                category ?
                  `${p.product_title} ${p.asin} ${p.sku} ${p.tags?.join(" ")}`
                : `${p.category_name} ${p.name}`;

              if (searchFilter.includes(",")) {
                const matchable = searchFilter.split(",");
                return matchable.some((searchCriteria) => {
                  return (
                    searchable
                      ?.replace(/\s/g, "")
                      .toLowerCase()
                      .includes(searchCriteria.replace(/\s/g, "").toLowerCase()) && searchCriteria.trim().length
                  );
                });
              }

              return searchable?.toLowerCase().includes(searchFilter.toLowerCase());
            }
            return true;
          }) ?? [],
      );
    },
    // Below linting disabled to take clearFilters in to recalcalculate data on filter reset
    // Without it, filter specs from react-table must be stored at this level and manually cleared on each reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stats, searchFilter, isExpanded, customFilters],
  );

  const createColumn = makeColumn(data, marketPlace, mobileStyle, date, setIsExpanded, modules);
  const standardMetricIds = useStandardEntityMetrics(category ? "product" : "category");
  const [columns, columnSelectorProps] = useColumnSelector(
    () => {
      let specifiedColumns = columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options));
      specifiedColumns = extendAllEntityMetricOptions(
        standardMetricIds,
        data,
        specifiedColumns,
        marketPlace.marketPlace,
      );

      return sortTableColumns(specifiedColumns, savedColumnOrder);
    },
    [standardMetricIds, columnSpecs, stats, mobileStyle, colToggle],
    TABLE_ID,
  );

  const columnOrder = columns.map((col) => col.id);

  if (isLoading) {
    return <Loading height={"500px"} fullPage={false} />;
  }

  return (
    <ReactTable
      pageSizeOptions={[5, 10, 20, 25, 50]}
      ref={tableRef}
      showPaginationBottom
      showPaginationTop
      PaginationComponent={ReactTablePagination}
      columns={columns}
      data={data}
      defaultPageSize={savedTablePageSize || 10}
      minRows={5}
      className={`-highlight ${TABLE_ID}`}
      defaultSorted={savedTableSortMethod || [{ id: "sales", desc: false }]}
      filterable={showFilters}
      state={columnOrder}
      onSortedChange={(newSorted) => {
        setSavedTableSortMethod(newSorted);
      }}
      onPageSizeChange={(pageSize) => {
        setSavedTablePageSize(pageSize);
      }}
      TrComponent={TrComponent}
      getTrProps={(table, row) => {
        const expandedChildren = <ExpandedContent marketPlace={marketPlace} row={row} user={user} channel={channel} />;
        return { row, expandedChildren };
      }}
      getTheadFilterProps={() => {
        return { style: { display: "flex" } };
      }}
      getPaginationProps={() => ({
        updateSearch: setSearchFilter,
        clearFilters: () => {
          setSearchFilter("");
        },
        hideFilters: false,
        showFilters: showFilters,
        setShowFilters: setShowFilters,
        clearSearchTermFilters: () => setSearchFilter(""),
        searchTerm: searchFilter,
        table: "plan-products",
        tableId: TABLE_ID,
        entityType: category ? "product" : "category",
        style: { marginBottom: "1rem" },
        carouselLayout: false,
        disableSearchFocus: true,
        filterControlsWidth: "70%",
        columnSelectorProps: columnSelectorProps,
        filterWidget: true,
        searchTagsInput: (
          <Tags
            filledTags={searchFilter.split(",")}
            saveFilledTags={(searches) => setSearchFilter(searches.join(","))}
            name={`Search for ${category ? "products" : "categories"}`}
            hideTagsField={true}
            hideEditButton={true}
            altButton={
              <i
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "10px",
                  fontSize: "15px",
                  opacity: ".8",
                  cursor: "pointer",
                }}
                className="fa fa-pencil"
              />
            }
          />
        ),
        hideFilterToggleIcon: true,
        savedColumnOrder: savedColumnOrder,
        setSavedColumnOrder: setSavedColumnOrder,
        children: (
          <div className="d-flex justify-content-start w-100 h-100 align-items-center px-2">
            <RT6FilterWidget
              customFilters={customFilters}
              setCustomFilters={setCustomFilters}
              tableRef={tableRef}
              additionalFilterOptions={
                category ?
                  [
                    INVENTORY_STATE_FILTER,
                    SALES_STATE_FILTER,
                    PRODUCT_WITH_ADS_FILTER,
                    buildCustomTagsFilter(customTags),
                  ]
                : []
              }
              excludedFilterColumns={category ? ["product_title"] : ["category_name"]}
              tableId={TABLE_ID}
            />
          </div>
        ),
      })}
    />
  );
};

const Dashboard = () => {
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const { category = null } = useParams();

  const mobileStyle = useMediaQuery("(max-width:600px)");
  const { group, date, start, end, preStart, preEnd } = useDates();
  const { preferences } = useMerchandisePreferences();
  const [annotationsTab, setAnnotationsTab] = useState("advertisement");
  const hasTop500Module = useModuleEnabled("TOP500");
  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  const { category: categoryId } = useParams();
  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    categoryId,
    "merchandising",
  );

  const filters = {
    mp: marketPlace.marketPlace,
    channel,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "category_id",
  };

  let [key, endpoint, group_by] = [["category_report"], "category_report", "category_id"];
  if (category) {
    key = ["product_report", category];
    endpoint = "product_report";
    group_by = "product_id";
    filters["category"] = category;
  }
  const tableFilters = { ...filters, group_by };
  const downloadFilters = {
    ...tableFilters,
    empty: false,
    pre_start_date: null,
    pre_end_date: null,
  };

  const {
    isSuccess,
    data: categoryReportData,
    isError,
    isLoading,
    isFetching,
  } = useFetch(
    ["category_report", marketPlace.marketPlace, start, end, preStart, preEnd, group, category],
    `/api/data_report/category_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const defaultVisibleColumns = ["sales", "units", "margin", "cost", "roms", "page_views", "conversion_rate"];
  const persistKey = "merchDashboardCategoryProducts";

  const standardMetricIds = useStandardEntityMetrics("product");

  const columns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        header: () => "Product",
        enableHiding: false,
        minSize: 100,
        size: 500,
        cell: (props) => {
          const { product_title } = props.row.original;

          return (
            <ProductCell
              original={{
                ...props.row.original,
                product_id: props.row.original.product_id.toString(),
                pricing_plan__status: props.row.original.pricing_plan_status,
                price_override_end:
                  props.row.original.price_override_end ? props.row.original.price_override_end.slice(0, 10) : "",
                cellContext: "merchandising",
              }}
              value={product_title}
              showInventoryBadge={false}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            label: "Product Title",
          },
        },
      }),
      columnHelper.accessor("parent_asin", {
        id: "parent_asin",
        header: "Parent ASIN",
        enableHiding: channel === "amazon",
        enableResizing: false,
        size: 110,
        cell: ({ getValue }) => {
          const asin = getValue();

          if (!asin) {
            return null;
          }

          return (
            <Flex gap={"small"} alignItems={"center"}>
              <span>{asin}</span>
              <span className={css({ paddingBottom: "xsmall" })}>
                <ExternalProductLink asin={asin} />
              </span>
            </Flex>
          );
        },
      }),
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row, {
        id: "inventory_stock_state",
        header: "Inventory State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor((row) => row, {
        id: "sales_state",
        header: "Sales State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "tags",
        header: "Tags",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "variations",
        header: "Variants",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor("pricing_plan_status", {
        id: "pricing_plan_status",
        header: "Enrollment State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
      columnHelper.accessor("product_quantity_fba", {
        id: "product_quantity_fba",
        header: "Product Quantity FBA",
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      columnHelper.accessor("product_quantity", {
        id: "product_quantity",
        header: "Product Quantity",
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      columnHelper.accessor("trends_score", {
        id: "trends_score",
        header: "Trends",
        enableResizing: false,
        size: 75,
        cell: (props) => {
          const { margin_trend, sales_trend, trends_score } = props.row.original;

          const trends = [];

          if (margin_trend && margin_trend >= 1) {
            trends.push("Margin is trending up");
          }
          if (margin_trend && margin_trend <= -1) {
            trends.push("Margin is trending down");
          }

          if (sales_trend && sales_trend >= 1) {
            trends.push("Sales are trending up");
          }
          if (sales_trend && sales_trend <= -1) {
            trends.push("Sales are trending down");
          }
          return <TrendCell value={trends_score} trends={trends} />;
        },
      }),
    ],
    [standardMetricIds, channel],
  );

  const defaultHiddenCols = idsToVisibilityState(
    defaultVisibleColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id),
  );

  const initialStateConfig = {
    columnVisibility: defaultHiddenCols,
    columnOrder: defaultVisibleColumns,
    columnPinning: {
      left: ["product_title"],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: persistKey,
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const productQueryFilters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length ? [{ id: "product_id", value: { op: "anyof", value: selectedIds } }] : [];
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    return [...state.columnFilters, ...selectedIdsFilter, ...categoryFilter];
  }, [state.columnFilters, selectedIds, categoryId]);

  // Fetch product report data grouped by date to populate chart
  const byDateOptionsGetter = useGetV4ProductReportByDate();
  const productsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: productQueryFilters,
      search: deferredSearch,
    }),
  );
  const totalOptionsGetter = useGetV4ProductReportTotals();
  const productTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: productQueryFilters,
      search: deferredSearch,
    }),
  );

  const data = useMemo(() => {
    if (category) {
      if (
        (selectedIds.length || deferredSearch || productQueryFilters.length > 1) &&
        productTotalsReportQuery.data?.data &&
        productsByDateReportQuery.data?.data
      ) {
        return {
          ...categoryReportData,
          total: productTotalsReportQuery.data?.data,
          date: productsByDateReportQuery.data?.data,
        };
      }
      return categoryReportData;
    }

    return categoryReportData;
  }, [
    category,
    categoryReportData,
    selectedIds.length,
    deferredSearch,
    productQueryFilters.length,
    productTotalsReportQuery.data?.data,
    productsByDateReportQuery.data?.data,
  ]);

  const topProductsDownloadFilters = {
    ...tableFilters,
    group_by: "product_id",
    top_n: 500,
  };

  const { data: topProductsData, isLoading: topProductsDataLoading } = useFetch(
    ["top_products", marketPlace.marketPlace, start, end, preStart, preEnd, group],
    `/api/data_report/product_report`,
    { ...topProductsDownloadFilters },
    {
      select: (d) => d.data?.products ?? [],
      placeholderData: keepPreviousData,
      enabled: hasTop500Module,
    },
  );

  const { data: tableData, isLoading: isTableDataLoading } = useFetch(
    [...key, marketPlace.marketPlace, start, end, preStart, preEnd, group],
    `/api/data_report/${endpoint}`,
    tableFilters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data: annotationData, isLoading: isAnnotationDataLoading } = useFetch(
    ["annotations", start, end, channel, category, marketPlace.marketPlace],
    "/api/annotations/",
    {
      groupby: group,
      ts_after: `${filters["start_date"]}T00:00:00`,
      ts_before: `${filters["end_date"]}T23:59:59`,
      marketplace: marketPlace.marketPlace,
      channel: channel === "amazon" ? "AMZ" : channel,
      category: category ?? null,
    },
    // ts_before and ts_after need values from categories to contain start and end date within query string
    {
      placeholderData: keepPreviousData,
    },
  );

  const acosOrRoas = useMemo(() => preferences?.acos_or_roas ?? "acos", [preferences]);

  const annotationCounts = useMemo(() => {
    const data = annotationData?.data?.map((date) => date.annotations)?.flat() ?? [];

    return data?.reduce(
      (acc, annotation) => {
        if (annotation.product) {
          acc.productCount++;
        }
        if (annotation.advertisement) {
          acc.advertisementCount++;
        }
        return acc;
      },
      { productCount: 0, advertisementCount: 0 },
    );
  }, [annotationData]);

  const { overviewMetrics, adMetrics, contentMetrics, topNProductsMetrics } = useMemo(() => {
    const titleCol =
      category ?
        [
          col("", "expander", "isExpanded", {
            width: 60,
            hideFilter: true,
            isStatic: true,
          }),
          col("Product", "product", "product_title", {
            className: "text-left font-weight-bold w-auto",
            width: 400,
            mobileWidth: 250,
            showDot: false,
            fixedLeft: true,
            hideFilter: true,
            isStatic: true,
          }),
        ]
      : [
          col("Category", "category", "category_name", {
            className: "text-left font-weight-bold",
            width: 400,
            mobileWidth: 250,
            showDot: false,
            fixedLeft: true,
            hideFilter: true,
            isStatic: true,
          }),
        ];

    if (channel === "shopify") {
      return {
        topNProductsMetrics: {},
        overviewMetrics: {
          columns: [
            ...titleCol,
            col("Sales", "currency", "total_sales"),
            col("Units", "number", "total_units"),
            col("Margin", "percent", "margin", {
              delta: true,
            }),
          ],
          metrics: [
            buildMetric(marketPlace, data, "sales", "sales_percent"),
            buildMetric(marketPlace, data, "units", "units_percent"),
            buildMetric(marketPlace, data, "margin", "margin_delta"),
          ],
        },
        adMetrics: {},
        contentMetrics: {},
      };
    }
    const additionalColumns =
      category ?
        [
          col("Ad Sales Same SKU", "currency", "ad_sales_same_sku", {
            checked: false,
          }),
          col("ROAS Same SKU", "number", "roas_same_sku", {
            checked: false,
          }),
          col("ACOS Same SKU", "percent", "acos_same_sku", {
            checked: false,
          }),
          col("BSR", "number", "last_bsr_rank", {
            checked: false,
            accessor: "last_bsr_rank",
            isNegative: true,
            delta: true,
          }),
          col("Page Views", "number", "page_views", {
            checked: true,
          }),
          col("Conversion Rate", "percent", "conv_rate", {
            checked: true,
          }),
          col("Current Inventory FBA", "number", "product_quantity_fba", {
            checked: false,
            hideCompare: true,
          }),
          col("Current Inventory MFA", "number", "product_quantity", {
            checked: false,
            hideCompare: true,
          }),
        ]
      : [];

    const roasMetrics = [
      buildMetric(marketPlace, data, "troas", "troas_delta", {
        metric: {
          decimalPlaces: 1,
        },
      }),
      buildMetric(marketPlace, data, "roas", "roas_delta", {
        metric: {
          decimalPlaces: 1,
        },
      }),
    ];
    const acosMetrics = [
      buildMetric(marketPlace, data, "total_acos", "total_acos_delta", {
        metric: {
          decimalPlaces: 1,
        },
      }),
      buildMetric(marketPlace, data, "acos", "acos_delta", {
        metric: {
          decimalPlaces: 1,
        },
      }),
    ];

    const acosCol = [
      col("ACOS", "percent", "acos", {
        noColor: true,
        delta: true,
        checked: false,
      }),
      col("Total ACOS", "percent", "total_acos", {
        noColor: true,
        delta: true,
        checked: false,
      }),
    ];

    const roasCol = [
      col("ROAS", "number", "roas", {
        noColor: true,
        delta: true,
        checked: false,
      }),
      col("TROAS", "number", "troas", {
        noColor: true,
        delta: true,
        checked: false,
      }),
    ];

    return {
      topNProductsMetrics: {
        columns: [
          col("", "expander", "isExpanded", {
            width: 60,
            hideFilter: true,
            isStatic: true,
          }),
          col("Product", "product", "product_title", {
            className: "text-left font-weight-bold w-auto",
            width: 400,
            mobileWidth: 250,
            showDot: false,
            fixedLeft: true,
            hideFilter: true,
            isStatic: true,
          }),
          col("Sales", "currency", "total_sales"),
          col("Units", "number", "total_units"),
          col("Margin", "percent", "margin", {
            delta: true,
          }),
          ...(acosOrRoas === "acos" ? acosCol : roasCol),
          col("Ad Sales", "currency", "ad_sales", { checked: false }),
          col("Ad Spend", "currency", "cost", {
            noColor: true,
            checked: false,
          }),
          ...(acosOrRoas === "acos" ? roasCol : acosCol),
          col("Discounts", "currency", "promo_cost", {
            noColor: true,
            checked: false,
          }),
          col("Cost", "currency", "total_cost", { noColor: true }),
          col("ROMS", "number", "roms", {
            noColor: true,
            delta: true,
          }),
          ...additionalColumns,
          col("Trends", "trends", "trends_score", {
            className: "text-center",
            checked: false,
          }),
        ],
        metrics: [
          buildMetric(marketPlace, data, "sales", "sales_percent"),
          buildMetric(marketPlace, data, "units", "units_percent"),
          buildMetric(marketPlace, data, "margin", "margin_delta"),
          ...(acosOrRoas === "acos" ? acosMetrics : roasMetrics),
          buildMetric(marketPlace, data, "ad_sales", "ad_sales_percent"),
          buildMetric(marketPlace, data, "ad_spend", "ad_spend_percent"),
          ...(acosOrRoas === "acos" ? roasMetrics : acosMetrics),
          buildMetric(marketPlace, data, "roms", "roms_delta"),
          buildMetric(marketPlace, data, "cost", "cost_percent"),
          buildMetric(marketPlace, data, "promo_cost", "promo_cost_percent"),
          buildMetric(marketPlace, data, "ctr", "ctr_delta"),
          buildMetric(marketPlace, data, "orders", "orders_percent"),
          buildMetric(marketPlace, data, "cpc", "cpc_delta"),
          buildMetric(marketPlace, data, "conversion_rate", "conversion_rate_delta"),
        ],
      },
      overviewMetrics: {
        columns: [
          ...titleCol,
          col("Sales", "currency", "total_sales"),
          col("Units", "number", "total_units"),
          col("Margin", "percent", "margin", {
            delta: true,
          }),
          ...(acosOrRoas === "acos" ? acosCol : roasCol),
          col("Ad Sales", "currency", "ad_sales", { checked: false }),
          col("Ad Spend", "currency", "cost", {
            noColor: true,
            checked: false,
          }),
          ...(acosOrRoas === "acos" ? roasCol : acosCol),
          col("Discounts", "currency", "promo_cost", {
            noColor: true,
            checked: false,
          }),
          col("Cost", "currency", "total_cost", { noColor: true }),
          col("ROMS", "number", "roms", {
            noColor: true,
            delta: true,
          }),
          ...additionalColumns,
          col("Trends", "trends", "trends_score", {
            className: "text-center",
            checked: false,
          }),
        ],
        metrics: [
          buildMetric(marketPlace, data, "sales", "sales_percent"),
          buildMetric(marketPlace, data, "units", "units_percent"),
          buildMetric(marketPlace, data, "margin", "margin_delta"),
          ...(acosOrRoas === "acos" ? acosMetrics : roasMetrics),
          buildMetric(marketPlace, data, "ad_sales", "ad_sales_percent"),
          buildMetric(marketPlace, data, "ad_spend", "ad_spend_percent"),
          ...(acosOrRoas === "acos" ? roasMetrics : acosMetrics),
          buildMetric(marketPlace, data, "roms", "roms_delta"),
          buildMetric(marketPlace, data, "cost", "cost_percent"),
          buildMetric(marketPlace, data, "promo_cost", "promo_cost_percent"),
          buildMetric(marketPlace, data, "ctr", "ctr_delta"),
          buildMetric(marketPlace, data, "orders", "orders_percent"),
          buildMetric(marketPlace, data, "cpc", "cpc_delta"),
          buildMetric(marketPlace, data, "conversion_rate", "conversion_rate_delta"),
        ],
      },
      adMetrics: {
        metrics: [
          buildMetric(marketPlace, data, "ad_sales", "ad_sales_percent"),
          buildMetric(marketPlace, data, "ad_spend", "ad_spend_percent"),
          buildMetric(marketPlace, data, "roas", "roas_delta", {
            metric: {
              decimalPlaces: 1,
            },
          }),
          buildMetric(marketPlace, data, "clicks", "clicks_percent"),
        ],
      },
      contentMetrics: {
        metrics: [
          buildMetric(marketPlace, data, "seo_score", "seo_score_percent"),
          buildMetric(marketPlace, data, "ctr", "ctr_delta"),
          buildMetric(marketPlace, data, "buyability_score", "buyability_score_percent"),
          buildMetric(marketPlace, data, "conversion_rate", "conversion_rate_delta"),
        ],
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, acosOrRoas, marketPlace, group]);

  const breadCrumbItems = [
    {
      name: "Merchandising Dashboard",
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: "Dashboards",
    },
  ];
  if (category) {
    breadCrumbItems.push({
      name: categoryDetails?.leaf_category_name,
      href: categoryDetails.href,
      dropdownLinkOptions: categoryData,
      searchable: true,
      searchPlaceholderText: "Search categories...",
      dropdownSubtitle: "Categories",
    });
  }

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  const cards = useMemo(() => {
    const settings = user.preferences?.merchandise_dashboard ?? {};
    return CARDS.map((c) => ({
      ...c,
      enabled: settings?.[c.id] ?? true,
    })).filter((c) => checkApplication(user, c.app));
  }, [user]);

  const updateMerchandisePreferences = useMutation({
    mutationFn: async ({ id, enabled }) => {
      const settings = user.preferences?.merchandise_dashboard ?? {};
      const merchandise_dashboard = { ...settings, [id]: enabled };
      await api.put("/api/org_permissions/", { merchandise_dashboard });
      store.dispatch(setMerchandiseDashboard(merchandise_dashboard));
    },
  });

  const isSPAPIConnected = useIsSPAPIConnected(user, channel);

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      } else {
        return "COLLECTING_DATA";
      }
    }
    if (isSPAPIConnected) {
      return "COLLECTING_DATA";
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace, isSPAPIConnected]);

  if (user.organization_type === "vendor" && channel === "walmart") {
    return <Redirect to="/user/advertising/dashboard" />;
  }

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      {showEmpty ?
        <EmptyState type={showEmpty} />
      : <>
          <Row>
            <Col xs={12} className="mb-4">
              <BreadcrumbDates items={breadCrumbItems}>
                <NavDropdown
                  title={<i className="fa fa-cog" />}
                  className={cx(
                    "noarrow",
                    css({
                      margin: "0",
                      "&>a": {
                        padding: "0",
                        display: "flex",
                        gap: "small",
                        alignItems: "center",
                        _after: {
                          alignSelf: "center",
                          width: 0,
                          height: 0,
                          content: '""',
                          borderTop: "4px solid",
                          borderRight: "4px solid transparent",
                          borderBottom: 0,
                          borderLeft: "4px solid transparent",
                        },
                      },
                    }),
                  )}
                >
                  {cards.map((c) => (
                    <div role="menuitem" className="py-1" key={c.id}>
                      <label
                        style={{
                          marginLeft: "1rem",
                          marginTop: "0.5rem",
                          fontWeight: "normal",
                          display: "flex",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{ height: "unset", marginRight: "1rem" }}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateMerchandisePreferences.mutate({
                              id: c.id,
                              enabled: !c.enabled,
                            });
                          }}
                          checked={c.enabled}
                        />
                        {c.name}
                      </label>
                    </div>
                  ))}
                </NavDropdown>
              </BreadcrumbDates>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="px-3 mb-4">
              {isLoading && <Loading />}
              {isError && <p>Error Loading Dashboard</p>}
              {isSuccess && (
                <Row>
                  <Col xs={12} className="mb-4">
                    <MetricsCard
                      title={"Overview"}
                      byline={
                        <>
                          {start.format("MMMM Do YYYY")}
                          {" - "}
                          {end.format("MMMM Do YYYY")}
                          {isFetching && <i className="fa fa-spinner fa-spin"></i>}
                        </>
                      }
                      metrics={overviewMetrics.metrics}
                      data={data}
                      initialSelectedMetric={
                        channel !== "shopify" ?
                          ["sales", "units", acosOrRoas ? "tacos" : "troas", "margin"]
                        : ["sales", "units", "margin"]
                      }
                      loading={
                        isFetching ||
                        productTotalsReportQuery.isFetching ||
                        productsByDateReportQuery.isFetching ||
                        productsByDateReportQuery.isPlaceholderData
                      }
                      showSkeletonLoadingEffect={category}
                      showAnnotations={true}
                      group={group}
                      showMetricsSelector={channel !== "shopify"}
                      id={category ? "merchProductsTable" : "merchCategoriesTable"}
                      context={category ? "category" : "organization"}
                      extendMetrics={channel !== "shopify"}
                    >
                      <Col xs={12}>
                        {category ?
                          <Accordion title={"Products"} initialExpanded visible={category}>
                            <Row>
                              <Col xs={12}>
                                <ProductsTable
                                  categoryId={category}
                                  persistKey={persistKey}
                                  state={state}
                                  initialState={initialState}
                                  setters={setters}
                                  columns={columns}
                                />
                              </Col>
                            </Row>
                          </Accordion>
                        : <Accordion title={category ? "Products" : "Categories"} initialExpanded>
                            <Row>
                              <Col xs={12}>
                                <Table
                                  stats={category ? tableData?.products : tableData?.categories}
                                  isLoading={isTableDataLoading}
                                  ExpandedContent={ExpandedContent}
                                  marketPlace={marketPlace}
                                  mobileStyle={mobileStyle}
                                  columnSpecs={overviewMetrics.columns}
                                  date={date}
                                  category={category}
                                  user={user}
                                  channel={channel}
                                />
                              </Col>
                              <Col xs={12} className="text-right">
                                <DownloadReport
                                  url_path={category ? "product_report" : "category_report"}
                                  filters={downloadFilters}
                                  report_dl={category ? "product" : "category"}
                                />
                              </Col>
                            </Row>
                          </Accordion>
                        }
                        <Accordion title={"Top 500 Products"} visible={!category && hasTop500Module}>
                          <Row>
                            <Col xs={12}>
                              <Table
                                stats={topProductsData}
                                isLoading={topProductsDataLoading}
                                ExpandedContent={ExpandedContent}
                                marketPlace={marketPlace}
                                mobileStyle={mobileStyle}
                                columnSpecs={topNProductsMetrics.columns}
                                date={date}
                                user={user}
                                channel={channel}
                              />
                            </Col>
                            <Col xs={12} className="text-right">
                              <DownloadReport
                                url_path={"product_report"}
                                filters={topProductsDownloadFilters}
                                report_dl={"product"}
                              />
                            </Col>
                          </Row>
                        </Accordion>

                        <Accordion title={"Impact Analysis"} visible={channel !== "shopify"}>
                          <Row>
                            <Col xs={12} className="fs-standard col-12 justify-content-end d-flex pe-5">
                              <ButtonGroup>
                                {[
                                  {
                                    value: "advertisement",
                                    label: (
                                      <>
                                        Media Plans{" "}
                                        {annotationCounts["advertisementCount"] ?
                                          <Badge
                                            bsClass="badge px-2"
                                            className={
                                              annotationsTab === "advertisement" ? "bade bg-white text-fuchsia" : (
                                                `badge bg-trellis-fuchsia text-white`
                                              )
                                            }
                                            style={{ marginLeft: ".5rem" }}
                                            pill
                                          >
                                            {annotationCounts["advertisementCount"]}
                                          </Badge>
                                        : null}
                                      </>
                                    ),
                                  },

                                  {
                                    value: "product",
                                    label: (
                                      <>
                                        Products{" "}
                                        {annotationCounts["productCount"] ?
                                          <Badge
                                            bsClass="badge px-2"
                                            className={
                                              annotationsTab === "product" ? "badge bg-white text-fuchsia" : (
                                                "badge bg-trellis-fuchsia text-white"
                                              )
                                            }
                                            style={{ marginLeft: ".5rem" }}
                                            pill
                                          >
                                            {annotationCounts["productCount"]}
                                          </Badge>
                                        : null}
                                      </>
                                    ),
                                  },
                                ]?.map((group) => {
                                  const { value, label } = group;
                                  return (
                                    <Button
                                      key={value}
                                      onClick={() => setAnnotationsTab(value)}
                                      className="fs-6"
                                      variant={annotationsTab === value ? "contained" : null}
                                      color={annotationsTab === value ? "secondary" : null}
                                    >
                                      {label}
                                    </Button>
                                  );
                                })}
                              </ButtonGroup>
                            </Col>
                            <Col xs={12}>
                              <AnnotationsTable
                                data={annotationData?.data ?? []}
                                isLoading={isAnnotationDataLoading}
                                marketPlace={marketPlace.marketPlace}
                                annotationType={annotationsTab}
                              />
                            </Col>
                          </Row>
                        </Accordion>
                        <Accordion
                          title={"Sales By State"}
                          visible={marketPlace.marketPlace === "USA" && channel === "amazon"}
                        >
                          <Row>
                            <Col xs={12}>
                              <SalesMapUSA
                                start_date={start.format("YYYY-MM-DD")}
                                end_date={end.format("YYYY-MM-DD")}
                                marketPlace={marketPlace}
                                categoryId={category}
                              />
                            </Col>
                          </Row>
                        </Accordion>
                      </Col>
                    </MetricsCard>
                  </Col>
                  <Col xs={12} className="">
                    <Card className="p-3">
                      <Card.Body>
                        <Row>
                          {cards.find((c) => c.app === ADVERTISING)?.enabled && (
                            <DetailedMetricRow
                              title={"Advertising"}
                              byline={
                                <>
                                  <ThemeLine>Placement</ThemeLine>
                                  Create and manage media plans
                                </>
                              }
                              metrics={adMetrics.metrics}
                              data={data?.date}
                              icon={AdvertisingIcon}
                              to={
                                category ?
                                  `/user/advertising/dashboard/category/${category}`
                                : "/user/advertising/dashboard"
                              }
                            />
                          )}
                          {cards.find((c) => c.app === DEALS)?.enabled && (
                            <>
                              <Col className="border-top" xs={12}></Col>
                              <PromotionsCard marketPlace={marketPlace} category={category} />
                            </>
                          )}
                          {cards.find((c) => c.app === DYNAMIC_PRICING)?.enabled && (
                            <>
                              <Col className="border-top" xs={12}></Col>
                              <PricingCard marketPlace={marketPlace} category={category} />
                            </>
                          )}
                          {cards.find((c) => c.app === CONTENT)?.enabled && (
                            <>
                              <Col className="border-top" xs={12}></Col>
                              <ContentCard
                                metrics={contentMetrics.metrics}
                                data={data?.date}
                                marketPlace={marketPlace}
                                category={category}
                              />
                            </>
                          )}
                          {cards.find((c) => c.app === NEW_PRODUCT_LAUNCH && channel === "amazon")?.enabled && (
                            <>
                              <Col className="border-top" xs={12}></Col>
                              <NPICard marketPlace={marketPlace} category={category} />
                            </>
                          )}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </>
      }
    </Grid>
  );
};

const MerchDashboard = () => {
  const [showAvailableProfilesModal, setShowAvailableProfilesModal] = useState(false);
  const history = useHistory();

  if (history.location?.state?.from === "onboardingSummary" && !showAvailableProfilesModal) {
    setShowAvailableProfilesModal(true);
  }

  return (
    <DatesProvider>
      <VerifyEmailModal />
      {showAvailableProfilesModal ?
        <AvailableProfilesModal />
      : <ExtensionInfo />}
      <Dashboard />
    </DatesProvider>
  );
};

export default MerchDashboard;
